/**
 * UsuariosPage (`/admin/usuarios`, RF-M11-012, RF-AUT-04) — gestão de usuários.
 *
 * Lista usuários (GET /usuarios), convida novos (POST /usuarios), altera
 * perfil/status (PATCH /usuarios/{id}), exclui (DELETE /usuarios/{id}) e redefine o
 * autenticador (POST /usuarios/{id}/redefinir-mfa) — todas rotas admin do M1; as duas
 * últimas com confirmação e nunca na linha do próprio admin (ADR-052). Só admin
 * alcança a rota (guarda no App.tsx + backend valida o grupo, RN-GLOBAL-006).
 * Nenhum dado pessoal além do e-mail é exibido/logado (RN-M11-001).
 */

import { FormEvent, useState } from 'react';
import {
  useUsuarios,
  useCriarUsuario,
  useAtualizarUsuario,
  useExcluirUsuario,
  useRedefinirMfa,
} from '../hooks/useUsuarios';
import { Perfil, StatusUsuario, Usuario } from '../services/usuariosApi';
import { ApiError } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';

const PERFIS: readonly Perfil[] = ['consulta', 'editor', 'admin'];

type Acao = 'excluir' | 'redefinir';

/** A mensagem do servidor num 409 (o próprio usuário, o último admin); senão, um texto genérico. */
function mensagemDoErro(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    const msg = (err.body as { error?: { message?: unknown } } | undefined)?.error?.message;
    if (typeof msg === 'string' && msg.length > 0) {
      return msg;
    }
  }
  return 'Não foi possível concluir. Tente novamente.';
}

/** Confirmação da exclusão ou da redefinição do autenticador (ADR-052), com o e-mail em destaque. */
function ConfirmarAcao({
  acao,
  usuario,
  onFechar,
  onConcluir,
}: {
  acao: Acao;
  usuario: Usuario;
  onFechar: () => void;
  onConcluir: (mensagem: string) => void;
}): JSX.Element {
  const excluir = useExcluirUsuario();
  const redefinir = useRedefinirMfa();
  const [erro, setErro] = useState<string | null>(null);
  const ehExclusao = acao === 'excluir';
  const pendente = ehExclusao ? excluir.isPending : redefinir.isPending;
  const titulo = ehExclusao ? 'Excluir usuário' : 'Redefinir autenticador';

  async function confirmar(): Promise<void> {
    setErro(null);
    try {
      if (ehExclusao) {
        await excluir.executar(usuario.id);
        onConcluir(`Usuário ${usuario.email} excluído.`);
      } else {
        await redefinir.executar(usuario.id);
        onConcluir(`Autenticador redefinido: ${usuario.email} cadastra um novo no próximo acesso.`);
      }
    } catch (err) {
      setErro(mensagemDoErro(err));
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={titulo}>
      <div className="modal">
        <h2>{titulo}</h2>
        <p>
          <strong>{usuario.email}</strong>
        </p>
        {ehExclusao ? (
          <p>
            A pessoa deixa de entrar no sistema e sai da lista; o histórico do que ela fez continua guardado. Esta ação
            não pode ser desfeita. Para afastar alguém por um tempo, use Desativar.
          </p>
        ) : (
          <p>
            No próximo acesso, a pessoa cadastra um autenticador novo no celular (use quando ela perdeu ou trocou o
            aparelho).
          </p>
        )}
        {erro && (
          <p className="form-error" role="alert">
            {erro}
          </p>
        )}
        <div className="row-actions">
          <button type="button" className="btn btn-primary" disabled={pendente} onClick={() => void confirmar()}>
            {ehExclusao
              ? pendente
                ? 'Excluindo…'
                : 'Confirmar exclusão'
              : pendente
                ? 'Redefinindo…'
                : 'Confirmar redefinição'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onFechar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export function UsuariosPage(): JSX.Element {
  const { data, isLoading, isError } = useUsuarios();
  const { criar, isPending: criando, isError: erroCriar } = useCriarUsuario();
  const { atualizar, isPending: atualizando } = useAtualizarUsuario();
  const auth = useAuth();
  const [confirmando, setConfirmando] = useState<{ acao: Acao; usuario: Usuario } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [perfil, setPerfil] = useState<Perfil>('consulta');

  async function onConvidar(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await criar({ email, perfil });
      // Limpa os campos APENAS no sucesso; a falha já é sinalizada por `erroCriar`.
      setEmail('');
      setPerfil('consulta');
    } catch {
      // Rejeição tratada aqui para não virar unhandled promise rejection; a UI
      // já exibe o erro via `erroCriar` (React Query). Mantém os campos.
    }
  }

  return (
    <div>
      <h1 className="page-title">Usuários</h1>
      <p className="page-subtitle">
        Convide usuários, altere perfis, ative/desative, redefina o autenticador e exclua contas. Ações restritas
        ao Administrador.
      </p>
      {aviso && (
        <p className="notice notice-info" role="status">
          {aviso}
        </p>
      )}

      <section className="card">
        <h2>Convidar usuário</h2>
        <form className="form-inline" onSubmit={onConvidar}>
          <label className="field">
            <span>E-mail</span>
            <input
              type="email"
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </label>
          <label className="field">
            <span>Perfil</span>
            <select value={perfil} onChange={(ev) => setPerfil(ev.target.value as Perfil)}>
              {PERFIS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={criando}>
            {criando ? 'Convidando…' : 'Convidar'}
          </button>
        </form>
        {erroCriar && (
          <p className="form-error" role="alert">
            Não foi possível convidar o usuário. Verifique o e-mail e tente novamente.
          </p>
        )}
      </section>

      <section className="card">
        <h2>Usuários</h2>
        {isLoading && <p className="muted">Carregando usuários…</p>}
        {isError && <p className="notice">Não foi possível carregar os usuários.</p>}
        {data && data.data.length === 0 && <p className="muted">Nenhum usuário cadastrado.</p>}
        {data && data.data.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>
                    <select
                      aria-label={`Perfil de ${u.email}`}
                      value={u.perfil}
                      disabled={atualizando}
                      onChange={(ev) =>
                        void atualizar(u.id, { perfil: ev.target.value as Perfil })
                      }
                    >
                      {PERFIS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{u.status}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={atualizando}
                      onClick={() =>
                        void atualizar(u.id, {
                          status: (u.status === 'ativo' ? 'desativado' : 'ativo') as StatusUsuario,
                        })
                      }
                    >
                      {u.status === 'ativo' ? 'Desativar' : 'Ativar'}
                    </button>
                    {/* ADR-052: nunca na linha do próprio admin (o `sub` da sessão é o id do usuário). */}
                    {u.id !== auth.user?.sub && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          aria-label={`Redefinir autenticador de ${u.email}`}
                          onClick={() => {
                            setAviso(null);
                            setConfirmando({ acao: 'redefinir', usuario: u });
                          }}
                        >
                          Redefinir autenticador
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          aria-label={`Excluir ${u.email}`}
                          onClick={() => {
                            setAviso(null);
                            setConfirmando({ acao: 'excluir', usuario: u });
                          }}
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {confirmando && (
        <ConfirmarAcao
          acao={confirmando.acao}
          usuario={confirmando.usuario}
          onFechar={() => setConfirmando(null)}
          onConcluir={(mensagem) => {
            setConfirmando(null);
            setAviso(mensagem);
          }}
        />
      )}
    </div>
  );
}
